import type {
  WebsiteLeadAttribution,
  WebsiteLeadTouch,
} from '@/types/website-leads';

/**
 * First-touch attribution: where a visitor came from (campaign parameters, ad
 * click ids, external referrer, landing page), kept in their own browser and
 * sent only with a form they submit. No cookie is set.
 *
 * Storage follows the cookie banner (`cookie-consent-preferences`):
 *  - analytics or marketing accepted: localStorage, dropped after 90 days;
 *  - both explicitly refused: nothing is stored, and earlier copies are removed;
 *  - no choice yet: sessionStorage only, gone with the tab.
 *
 * Every storage call is wrapped: storage can be blocked (private windows,
 * strict settings) and the site works the same without it.
 */

const STORAGE_KEY = 'bb_attribution_v1';
const CONSENT_KEY = 'cookie-consent-preferences';
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const VALUE_MAX = 255;
const URL_MAX = 2048;

/** Dispatched by the cookie banner when the visitor saves a choice. */
export const COOKIE_PREFERENCES_EVENT = 'cookie-preferences-updated';

/** URL parameter read for each touch field. */
const URL_PARAMS: Array<[string, keyof WebsiteLeadTouch]> = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
  ['gclid', 'gclid'],
  ['gbraid', 'gbraid'],
  ['wbraid', 'wbraid'],
  ['li_fat_id', 'liFatId'],
  ['fbclid', 'fbclid'],
  ['msclkid', 'msclkid'],
];

/**
 * Pages whose URL carries a secret (password reset, signing, unsubscribe,
 * preview and checkout links). Their address is never stored nor sent with a
 * lead, and a visit there is not a campaign touch.
 */
const UNTRACKED_PATH_PREFIXES = [
  '/auth/',
  '/sign/',
  '/agreements/',
  '/blog/preview/',
  '/newsletter/unsubscribe',
  '/invest-capital-print/',
  '/checkout/',
];

type Consent = 'granted' | 'refused' | 'unset';

interface StoredAttribution {
  /** The first page seen, written once per storage. */
  firstTouch: WebsiteLeadTouch;
  /** The latest later visit that came from a campaign or another site. */
  lastTouch?: WebsiteLeadTouch;
  expiresAt: string;
}

/** document.referrer describes how this page load started: read it once. */
let referrerRead = false;
/** Last address captured in this page load, so a repeated effect is a no-op. */
let lastCaptured: string | null = null;

function cap(value: string, max: number): string {
  const chars = Array.from(value);
  return chars.length > max ? chars.slice(0, max).join('') : value;
}

function readConsent(): Consent {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return 'unset';
    const prefs = JSON.parse(raw) as { analytics?: unknown; marketing?: unknown };
    if (prefs.analytics === true || prefs.marketing === true) return 'granted';
    if (prefs.analytics === false && prefs.marketing === false) return 'refused';
    return 'unset';
  } catch {
    return 'unset';
  }
}

function getStorage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readRecord(storage: Storage | null): StoredAttribution | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAttribution> | null;
    const expiresAt = Date.parse(String(parsed?.expiresAt));
    if (
      !parsed ||
      typeof parsed.firstTouch !== 'object' ||
      !parsed.firstTouch ||
      !Number.isFinite(expiresAt) ||
      expiresAt <= Date.now()
    ) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as StoredAttribution;
  } catch {
    return null;
  }
}

function writeRecord(storage: Storage | null, record: StoredAttribution): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota exceeded or storage blocked: attribution is best effort.
  }
}

function removeRecord(storage: Storage | null): void {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was stored.
  }
}

/** The referrer when it is another site; internal navigation is not a source. */
function externalReferrer(): string | undefined {
  const raw = document.referrer;
  if (!raw) return undefined;
  try {
    return new URL(raw).host !== window.location.host ? raw : undefined;
  } catch {
    return undefined;
  }
}

/** The current page as a touch, with the referrer only when asked for. */
function touchFromPage(withReferrer: boolean): WebsiteLeadTouch {
  const touch: WebsiteLeadTouch = {};
  const params = new URLSearchParams(window.location.search);
  for (const [param, field] of URL_PARAMS) {
    const value = params.get(param)?.trim();
    if (value) touch[field] = cap(value, VALUE_MAX);
  }
  const referrer = withReferrer ? externalReferrer() : undefined;
  if (referrer) touch.referrer = cap(referrer, URL_MAX);
  touch.landingPage = cap(
    `${window.location.pathname}${window.location.search}`,
    URL_MAX
  );
  touch.seenAt = new Date().toISOString();
  return touch;
}

/** A visit worth a last touch: campaign parameters, a click id or another site. */
function isCampaignTouch(touch: WebsiteLeadTouch): boolean {
  return (
    Boolean(touch.referrer) ||
    URL_PARAMS.some(([, field]) => Boolean(touch[field]))
  );
}

/**
 * Record the current page. Called on every route change by AttributionCapture:
 * the first page seen becomes the first touch, and a later page carrying a
 * campaign, a click id or an external referrer replaces the last touch.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  const address = `${pathname}${search}`;
  if (address === lastCaptured) return;
  lastCaptured = address;
  const withReferrer = !referrerRead;
  referrerRead = true;
  if (UNTRACKED_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return;
  }

  const consent = readConsent();
  const local = getStorage('local');
  const session = getStorage('session');
  if (consent === 'refused') {
    removeRecord(local);
    removeRecord(session);
    return;
  }

  let record: StoredAttribution | null;
  if (consent === 'granted') {
    // A record started in this tab before the choice moves to localStorage.
    record = readRecord(local) ?? readRecord(session);
    removeRecord(session);
  } else {
    // No choice yet: no persistent copy may remain.
    record = readRecord(session);
    removeRecord(local);
  }

  const touch = touchFromPage(withReferrer);
  if (!record) {
    record = {
      firstTouch: touch,
      expiresAt: new Date(Date.now() + RETENTION_MS).toISOString(),
    };
  } else if (isCampaignTouch(touch)) {
    record = { ...record, lastTouch: touch };
  }
  writeRecord(consent === 'granted' ? local : session, record);
}

/** Apply a choice just saved in the cookie banner. */
export function applyAttributionConsent(): void {
  if (typeof window === 'undefined') return;
  const consent = readConsent();
  const local = getStorage('local');
  const session = getStorage('session');
  if (consent === 'refused') {
    removeRecord(local);
    removeRecord(session);
    return;
  }
  if (consent === 'granted') {
    const record = readRecord(local) ?? readRecord(session);
    if (record) writeRecord(local, record);
    removeRecord(session);
  }
}

/**
 * Touches to send with a form. When nothing is stored (choice refused, storage
 * blocked), the current page stands in as the first touch, so a submission
 * still carries its own page, parameters and referrer.
 */
export function getAttributionForSubmit(): WebsiteLeadAttribution {
  if (typeof window === 'undefined') return {};
  const consent = readConsent();
  let record: StoredAttribution | null = null;
  if (consent === 'granted') {
    record = readRecord(getStorage('local')) ?? readRecord(getStorage('session'));
  } else if (consent === 'unset') {
    record = readRecord(getStorage('session'));
  }
  if (!record) return { firstTouch: touchFromPage(true) };
  return record.lastTouch
    ? { firstTouch: record.firstTouch, lastTouch: record.lastTouch }
    : { firstTouch: record.firstTouch };
}
